import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MainOfficeView from './MainOfficeView';
import IconRail from './IconRail';
import CalendarPanel from './panels/CalendarPanel';
import ExpensesPanel from './panels/ExpensesPanel';
import AgendaPanel from './panels/AgendaPanel';
import BoardsPanel from './panels/BoardsPanel';
import TelegramPanel from './panels/TelegramPanel';
import NotesPanel, { RemindersPanel } from './panels/NotesPanel';
import RulebookPanel from './panels/RulebookPanel';
import EnergyPanel from './panels/EnergyPanel';
import SettingsModal from './modals/SettingsModal';
import DocumentsModal from './modals/DocumentsModal';
import { UploadManagerProvider } from '../contexts/UploadManagerContext';
import { usePanelState } from '../hooks/usePanelState';
import { usePanelSize } from '../hooks/usePanelSize';
import { useTheme } from '../hooks/useTheme';
import { useIsMobile } from '../hooks/useIsMobile';
import { railMustCompact } from '../utils/slotGeometry';
import useAuth from '../hooks/useAuth';
import useLiveEvents from '../hooks/useLiveEvents';
import { useNotificationFeed } from '../hooks/useNotificationFeed';
import { useLanguage } from '../i18n';

/**
 * Is the keystroke destined for a text-entry surface?
 *
 * Only the LETTER shortcuts consult this — Cmd+B (Rulebook) and Cmd+E (Energy).
 * Cmd+B is the native/muscle-memory BOLD command inside a contentEditable
 * surface — the WYSIWYG RichTextEditor behind board ticket descriptions, ticket
 * comments and the note editor — so intercepting it globally would swallow the
 * bold and could yank the user out of an unsaved edit; Cmd+E is likewise a
 * native text-editing binding on macOS. The digit shortcuts have no such
 * collision, which is why the handler needed no guard until a letter joined it.
 *
 * `isContentEditable` is INHERITED, so it is already true for any element nested
 * inside an editable region; the `closest()` check is a belt-and-braces fallback
 * for hosts where the property is unavailable.
 */
const isEditableTarget = (t) => {
    if (!t) return false;
    if (t.isContentEditable) return true;
    if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT') return true;
    return typeof t.closest === 'function' && !!t.closest('[contenteditable="true"]');
};

/**
 * Top-level authed shell.
 *
 *   [Features Sidebar] [ (ConvColumn) | occupant A | Divider | occupant B ]
 *
 * The workspace holds AT MOST TWO occupants (the slot model — see
 * `utils/workspaceSlots.js`). An occupant is the Main Chat or a feature panel;
 * the chat is a closable occupant rather than a permanent anchor (#361), so any
 * two features can sit side by side once it is closed.
 *
 * The occupants are rendered inside MainOfficeView, which owns the conversation
 * column — the chat occupant's own furniture, hidden whenever the chat is closed
 * or covered (#359/#360). The chat pane itself is never unmounted: all the
 * fire-and-forget machinery (in-flight turns, per-conversation buffers, drafts,
 * origin-bound uploads) lives there, so "closed" is `display:none` and "covered"
 * is `visibility:hidden`.
 *
 * Settings and the File Manager are centered glass modals — they are overlays,
 * not slot occupants (#355). /dashboard bypasses the shell via App.jsx routing.
 */
// Billing-return toast copy — its own tiny component so the shell body doesn't
// need the language hook threaded through its (large) render.
function BillingToastText({ kind }) {
    const { t } = useLanguage();
    if (kind === 'cancelled') return t('billingCancelled', 'Checkout cancelled — no changes made.');
    if (kind === 'done') return t('billingSuccessDone', 'Your salary is active.');
    return t('billingSuccessPending', 'Payment received — activating your salary…');
}

export default function WorkspaceShell({ onLogout, sessionUser = null }) {
    const {
        activePanel, panelMode, openPanel, closePanel, togglePanel, setPanelMode,
        openFeatures, chatOpen, expandedOccupant, closeOccupant, toggleChat, setExpandedOccupant,
    } = usePanelState();
    const { splitRatio, setSplitRatio } = usePanelSize();
    const { theme, toggleTheme } = useTheme();
    const isMobile = useIsMobile();
    // #432: the viewport width, for the one shell decision that depends on it
    // beyond the mobile/desktop switch — whether the Features Sidebar must go
    // icon-only so two features fit side by side (utils/slotGeometry.js).
    const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));
    useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    // #543: this shell's own useAuth() starts at user=null and only fills in
    // after ITS Firebase + /auth/profile round-trip (2–3 s) — opening the Main
    // Chat from the launcher (which renders outside the shell) mounted a fresh
    // shell and the rail showed "U / Account" until then. App's instance already
    // holds the loaded profile (ProtectedRoute waits for it), so it is the
    // display fallback. Display only: the onboarding gates stay in App (#402).
    const { user: liveUser } = useAuth();
    const user = liveUser || sessionUser || null;
    const navigate = useNavigate();
    const location = useLocation();

    // Cross-channel live updates ("live update" #251). Mounted here — the shell
    // is the only component alive for the whole authed session (panels unmount
    // when closed) — so a change made on Telegram reaches whichever
    // module happens to be open. Emits window events only; it holds no state, so
    // the common empty poll re-renders nothing.
    useLiveEvents(true);

    // The notification feed (#437). The Bell left the Main Chat header and became
    // the Notifications column of "Reminders & Notifications"; its data lives
    // HERE so the sidebar's red dot and the column read one list. It polls on the
    // Bell's old 2-minute cadence, tightened to 15s while the column is on screen
    // so an arrival shows without reopening anything.
    const notificationsOpen = openFeatures.includes('reminders');
    const notificationFeed = useNotificationFeed({ fastPollMs: notificationsOpen ? 15_000 : null });
    const { markAllSeen: markNotificationsSeen, items: notificationItems } = notificationFeed;
    // The red dot clears the moment the feature is entered — and while it stays
    // open, whatever arrives is on screen, so it is seen too.
    useEffect(() => {
        if (notificationsOpen) markNotificationsSeen();
    }, [notificationsOpen, notificationItems, markNotificationsSeen]);
    const hasUnseenNotifications = !notificationsOpen && notificationFeed.unseenCount > 0;

    // Stripe billing return (payments #297-#299): Checkout/Portal send the user
    // back to the app root with ?billing=success|cancelled|managed. Consume the
    // param (strip it so a reload doesn't re-toast), and on success poll the
    // profile briefly — the plan flips when the WEBHOOK lands, usually within a
    // couple of seconds — so the toast can confirm the salary is active.
    const [billingToast, setBillingToast] = useState(null);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const outcome = params.get('billing');
        if (!outcome) return;
        params.delete('billing');
        const rest = params.toString();
        window.history.replaceState(
            {}, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash,
        );
        if (outcome === 'cancelled') {
            setBillingToast({ kind: 'cancelled' });
            const timer = setTimeout(() => setBillingToast(null), 6000);
            return () => clearTimeout(timer);
        }
        if (outcome !== 'success') return;   // 'managed' (portal return) — no toast
        setBillingToast({ kind: 'pending' });
        let cancelled = false;
        let tries = 0;
        const initialPlan = null;
        // #336: announce the flip so an ALREADY-OPEN Settings modal refreshes its
        // Salary section in place. This poll is the only thing in the app that
        // knows the moment a tier becomes real, and it previously kept that to
        // itself — it only ever set its own toast — so a modal left open across
        // the Stripe return kept showing the old tier until it was reopened.
        const announcePlanChange = () => {
            try {
                window.dispatchEvent(new CustomEvent('omnis:plan-updated'));
            } catch { /* a notification must never break the toast */ }
        };
        const poll = async (prevPlan) => {
            if (cancelled) return;
            tries += 1;
            try {
                const apiService = (await import('../services/apiService')).default;
                const profile = await apiService.getUserProfile();
                const plan = profile?.plan_status?.plan || 'free';
                if (prevPlan === initialPlan && tries === 1) {
                    // First read is the baseline; the webhook may already have landed.
                    if (plan !== 'free') {
                        setBillingToast({ kind: 'done' });
                        announcePlanChange();
                        setTimeout(() => setBillingToast(null), 6000);
                        return;
                    }
                } else if (plan !== 'free' && plan !== prevPlan) {
                    setBillingToast({ kind: 'done' });
                    announcePlanChange();
                    setTimeout(() => setBillingToast(null), 6000);
                    return;
                }
                if (tries < 10) setTimeout(() => poll(plan), 2000);
                else setBillingToast(null);
            } catch {
                if (tries < 10) setTimeout(() => poll(prevPlan), 2000);
                else setBillingToast(null);
            }
        };
        poll(initialPlan);
        return () => { cancelled = true; };
    }, []);

    // Settings Update (#236): Settings has two structurally-separate views —
    // 'user' (account/identity/billing) and 'system' (app-wide prefs). null = closed.
    const [settingsMode, setSettingsMode] = useState(null);
    const openSettings = useCallback((mode = 'system') => setSettingsMode(mode), []);
    const [showDocuments, setShowDocuments] = useState(false);
    // Mobile Visual #171 — the app-like overlay menu open state (mobile only).
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // On a phone there is no room for a split column, so any open panel covers
    // the whole screen. Force the pathname occupant "expanded" regardless of the
    // URL's ?view param, and drop the expand/collapse control (meaningless
    // full-screen). A second feature sits underneath it, unreachable until the
    // first is closed — mobile has one view, by contract (#171).
    const effectiveExpanded = isMobile ? (activePanel || null) : expandedOccupant;

    const lastConvId = () => {
        try { return localStorage.getItem('omnis:last-conversation-id'); } catch (_) { return null; }
    };

    // #355 — the File Manager is a MODAL overlay, not a slot occupant, so no route
    // renders it. A stray /documents (an old bookmark, a shared link, the launcher
    // tile before this fix) used to fall through the catch-all route and silently
    // land the user in the Main Chat. Consume it the way the panels consume their
    // deep links: act on it, then strip it from the URL.
    useEffect(() => {
        if (location.pathname !== '/documents') return;
        setShowDocuments(true);
        const conv = lastConvId();
        navigate(conv ? `/chat/${conv}` : '/', { replace: true });
    }, [location.pathname, navigate]);

    const handleTogglePanel = useCallback((panelName) => {
        // Documents is a centered modal, not a split panel — intercept here
        // so the rail's button still feels consistent (toggle on click).
        if (panelName === 'documents') {
            setShowDocuments((v) => !v);
            return;
        }
        const opts = { returnToConversationId: lastConvId() };
        // Rulebook opens expanded: it renders TWO panes (Folio + Assistant Desk)
        // and its 520px side-by-side threshold is wider than a docked split column.
        //
        // The day channels USED to open expanded too (the #38 "full-screen
        // channel" mockup), which made them the modules that covered the chat
        // and any paired feature the moment they opened — and their Focus
        // workspace re-expanded on every day you opened, with no collapse control.
        // Telegram dual display (#444) makes them standard workspace modules: they
        // open in the split slot like Calendar/Finance/Boards, and expand only when
        // the user asks (the grid's column count is container-driven, so a docked
        // split column no longer crushes the day cards).
        if (panelName === 'rulebook') opts.extraParams = { view: 'expanded' };
        togglePanel(panelName, opts);
    }, [togglePanel]);

    const handleClosePanel = useCallback(() => {
        closePanel({ returnToConversationId: lastConvId() });
    }, [closePanel]);

    /** One occupant's X. Emptying the workspace routes to the launcher (#361). */
    const handleCloseOccupant = useCallback((name) => {
        closeOccupant(name, { returnToConversationId: lastConvId() });
    }, [closeOccupant]);

    // The sidebar's "Main Chat" row and the chat's own X are the same toggle: the
    // chat is an occupant like any other (#361).
    const handleToggleChat = useCallback(() => {
        toggleChat({ returnToConversationId: lastConvId() });
    }, [toggleChat]);

    const handleToggleExpand = useCallback(() => {
        setPanelMode(panelMode === 'expanded' ? 'split' : 'expanded');
    }, [panelMode, setPanelMode]);

    // Cmd+1..9 panel toggles, Cmd+B the Rulebook (the digit row is full),
    // Cmd+0 closes any panel, Cmd+, settings, Cmd+Shift+E expands/collapses
    // the active panel.
    useEffect(() => {
        const handleKey = (e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (!mod) return;
            if (e.key === ',') { e.preventDefault(); openSettings('system'); return; }
            // Cmd+0 closes the open FEATURE, never the chat — emptying the
            // workspace (and so leaving for the launcher) should be a deliberate
            // click on an X, not a stray keystroke.
            if (e.key === '0') { e.preventDefault(); if (activePanel) handleClosePanel(); return; }
            if (e.key === '1') { e.preventDefault(); handleTogglePanel('documents'); return; }
            if (e.key === '2') { e.preventDefault(); handleTogglePanel('calendar'); return; }
            if (e.key === '3') { e.preventDefault(); handleTogglePanel('expenses'); return; }
            if (e.key === '4') { e.preventDefault(); handleTogglePanel('agenda'); return; }
            if (e.key === '5') { e.preventDefault(); handleTogglePanel('boards'); return; }
            // Cmd+6 is deliberately FREE: it used to open the SOUL/USER
            // editor, removed with #147 ("Soulfile"). Cmd+7 is free too (#530). The remaining digits
            // keep their existing bindings — renumbering them would break
            // muscle memory for a shortcut nobody asked to move.
            if (e.key === '8') { e.preventDefault(); handleTogglePanel('telegram'); return; }
            if (e.key === '9') { e.preventDefault(); handleTogglePanel('notes'); return; }
            // Cmd+B ("Book") — the digit row is fully assigned (0 closes any
            // panel, 1..9 are the nine older modules), so the Rulebook takes the
            // first free mnemonic letter (PAI Rulebook #146). Being a LETTER, it
            // collides with the native BOLD shortcut, so it YIELDS whenever the
            // keystroke belongs to a text field or a contentEditable editor —
            // no preventDefault, so bold still happens.
            if (e.key === 'b' || e.key === 'B') {
                if (isEditableTarget(e.target)) return;
                e.preventDefault();
                handleTogglePanel('rulebook');
                return;
            }
            if (e.shiftKey && (e.key === 'e' || e.key === 'E') && activePanel) {
                e.preventDefault();
                handleToggleExpand();
                return;
            }
            // Cmd+E ("Energy") — the Energy Hub (Energy HUB part 1). Checked
            // AFTER the expand shortcut and explicitly requiring NO Shift, so
            // Cmd+Shift+E can never fall through to it when no panel is open.
            // Like Cmd+B it is a LETTER, so it yields to text-entry surfaces
            // rather than stealing the keystroke mid-edit.
            if (!e.shiftKey && (e.key === 'e' || e.key === 'E')) {
                if (isEditableTarget(e.target)) return;
                e.preventDefault();
                handleTogglePanel('energy');
                return;
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [handleTogglePanel, handleClosePanel, handleToggleExpand, activePanel, openSettings]);

    /**
     * One occupant's panel. Each gets its OWN chrome handlers — with two open,
     * "close" and "expand" have to mean *this* one (#358/#359).
     */
    const renderPanel = (name) => {
        const shared = {
            mode: effectiveExpanded === name ? 'expanded' : 'split',
            onClose: () => handleCloseOccupant(name),
            // No split/expand toggle on mobile — panels are always full-screen.
            onToggleExpand: isMobile
                ? undefined
                : () => setExpandedOccupant(expandedOccupant === name ? null : name),
        };
        switch (name) {
            case 'calendar': return <CalendarPanel {...shared} />;
            case 'expenses': return <ExpensesPanel {...shared} />;
            case 'agenda': return <AgendaPanel {...shared} user={user} />;
            case 'boards': return <BoardsPanel {...shared} />;
            case 'telegram': return <TelegramPanel {...shared} />;
            case 'notes': return <NotesPanel {...shared} />;
            case 'reminders': return <RemindersPanel {...shared} notificationFeed={notificationFeed} />;
            case 'rulebook': return <RulebookPanel {...shared} />;
            case 'energy': return <EnergyPanel {...shared} />;
            default: return null;
        }
    };

    // Display order comes from the slot model, so the shell never re-derives it.
    const featurePanels = openFeatures.map((name) => ({ name, node: renderPanel(name) }));

    // Every surface with an indicator in the sidebar (#358). The File Manager is a
    // modal rather than a slot occupant, so it lights up independently of the
    // two-occupant cap.
    const activePanels = useMemo(() => {
        const set = new Set(openFeatures);
        if (chatOpen) set.add('chat');
        if (showDocuments) set.add('documents');
        return set;
    }, [openFeatures, chatOpen, showDocuments]);

    return (
        <UploadManagerProvider>
        {/* The wallpaper root. Every panel above it is translucent, so no
        panel may rely on the photograph for contrast — all text sits on
        the panel's own alpha-white background. */}
        <div className="relative h-dvh overflow-hidden bg-pai-void text-white">
        <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/wallpaper.jpg')" }}
        />
        <div aria-hidden="true" className="absolute inset-0 bg-[rgba(5,7,10,0.64)]" />

{/* The frame. It used to carry a 1280px floor that scrolled
sideways rather than compressing — which put every tablet's
right-hand controls outside the viewport (#432). The desktop
shell now starts at 1024px (`shell:`, utils/layoutBreakpoints.js)
and the slot model fits itself to the width it gets (the
conversation column folds away when the floors cannot hold);
below that the shell is the full-bleed mobile one (#171). At
≥1280px nothing changed: the floor was a no-op there. */}
<div className="relative h-full w-full overflow-x-auto overflow-y-hidden">
<div className="flex h-full shell:gap-[14px] shell:p-[14px]">
                <IconRail
                    compact={!isMobile && railMustCompact(viewportWidth, {
                        featureCount: openFeatures.length,
                        chatOpen,
                        expanded: !!effectiveExpanded,
                    })}
                    activePanels={activePanels}
                    onTogglePanel={handleTogglePanel}
                    onOpenSettings={openSettings}
                    onOpenLauncher={() => navigate('/home')}
                    onToggleMainChat={handleToggleChat}
                    user={user}
                    unseenPanels={hasUnseenNotifications ? ['reminders'] : []}
                />

                <div className="flex-1 min-w-0">
                    <MainOfficeView
                        activePanel={activePanel}
                        chatOpen={chatOpen}
                        onCloseChat={handleToggleChat}
                        featurePanels={featurePanels}
                        expandedOccupant={effectiveExpanded}
                        splitRatio={splitRatio}
                        onSplitRatioChange={setSplitRatio}
                        isMobile={isMobile}
                        mobileMenuOpen={mobileMenuOpen}
                        onOpenMobileMenu={() => setMobileMenuOpen(true)}
                        onCloseMobileMenu={() => setMobileMenuOpen(false)}
                        onOpenLauncher={() => navigate('/home')}
                        onTogglePanel={handleTogglePanel}
                        onOpenSettings={openSettings}
                        activePanelName={showDocuments ? 'documents' : activePanel}
                        unseenPanels={hasUnseenNotifications ? ['reminders'] : []}
                        onLogout={onLogout}
                        sessionUser={user}
                    />
                    </div>
                    </div>
                </div>

                <SettingsModal
                    isOpen={!!settingsMode}
                    mode={settingsMode || 'system'}
                    onClose={() => setSettingsMode(null)}
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    onLogout={onLogout}
                    onOpenEnergyPanel={() => {
                        setSettingsMode(null);
                        openPanel('energy');
                    }}
                />

                <DocumentsModal
                    isOpen={showDocuments}
                    onClose={() => setShowDocuments(false)}
                />

                {/* Stripe billing return toast (payments #297-#299). */}
                {billingToast && (
                    <div
                        data-testid="billing-toast"
                        data-kind={billingToast.kind}
                        className="fixed bottom-4 right-4 z-[90] max-w-xs px-4 py-3 rounded-lg shadow-modal-sm border text-sm bg-white/[0.03] border-pai-subtle text-white"
                    >
                        <BillingToastText kind={billingToast.kind} />
                    </div>
                )}
            </div>
        </UploadManagerProvider>
    );
}
